'use client'

import { Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

import { PieChartSVGIcon } from '@/assets/svg'
import { type ChartConfig, ChartContainer } from '@/components/ui/chart'
import { EmptyState } from '@/components/fragments'

export const description = 'Restaurant performance donut chart'

interface CustomerPerformanceData {
  first_time_restaurants: {
    count: number
    percentage: number
  }
  repeat_restaurants: {
    count: number
    percentage: number
  }
}

interface CustomerPerformanceProps {
  data?: CustomerPerformanceData
}

const chartConfig = {
  'Repeat Customers': {
    label: 'Repeat Customers',
    color: '#1B5E20',
  },
  'First Time Users': {
    label: 'First Time Users',
    color: '#8FBC8F',
  },
} satisfies ChartConfig

export function CustomerPerformanceChart({ data }: CustomerPerformanceProps) {
  // Default data if no API data is provided
  const defaultData: CustomerPerformanceData = {
    first_time_restaurants: { count: 0, percentage: 0 },
    repeat_restaurants: { count: 0, percentage: 0 },
  }

  const performanceData = data || defaultData

  const chartData = [
    {
      category: 'Repeat Customers',
      count: performanceData.repeat_restaurants.count,
      percentage: performanceData.repeat_restaurants.percentage,
      fill: '#295532',
    },
    {
      category: 'First Time Users',
      count: performanceData.first_time_restaurants.count,
      percentage: performanceData.first_time_restaurants.percentage,
      fill: '#5A9B7A',
    },
  ]

  // Check if there's any data to display
  const hasData =
    performanceData.repeat_restaurants.count > 0 ||
    performanceData.first_time_restaurants.count > 0

  if (!hasData) {
    return (
      <div className='w-full h-full flex items-center justify-center'>
        <EmptyState
          variant='data'
          title='No Order Behavior Data'
          description='Order behavior data will appear here once customers start placing orders.'
          minHeight='300px'
        />
      </div>
    )
  }

  return (
    <div className='relative'>
      <ChartContainer
        config={chartConfig}
        className='mx-auto aspect-square max-h-[300px] relative z-10'
      >
        <ResponsiveContainer width='100%' height='100%'>
          <PieChart>
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow:
                  '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                zIndex: 1000,
                fontSize: '14px',
                padding: '8px 12px',
              }}
              labelStyle={{
                color: '#374151',
                fontWeight: '500',
              }}
              formatter={(value: number, name: string) => [
                `${value} customers (${chartData.find((item) => item.category === name)?.percentage}%)`,
                name,
              ]}
            />
            <Pie
              data={chartData}
              dataKey='count'
              nameKey='category'
              innerRadius={80}
              outerRadius={120}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className='absolute top-[50%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none -z-10'>
          <div className='w-30 h-30 bg-[#E9ECF1] rounded-full flex items-center justify-center'>
            <div className='w-8 h-8 rounded-md flex items-center justify-center'>
              <figure>{PieChartSVGIcon}</figure>
            </div>
          </div>
        </div>
      </ChartContainer>

      <div className='p-6 pt-0'>
        {/* Legend */}
        <div className='flex flex-col md:flex-row gap-4'>
          <div className='flex items-start gap-3'>
            <div className='flex-shrink-0 w-3 h-3 rounded-full bg-[#1B5E20] mt-1'></div>
            <div className='flex flex-col'>
              <span className='text-sm font-medium text-gray-900'>Repeat Customers</span>
              <span className='text-sm text-gray-600'>
                {performanceData.repeat_restaurants.percentage}% • {performanceData.repeat_restaurants.count.toLocaleString()}
              </span>
            </div>
          </div>
          <div className='flex items-start gap-3'>
            <div className='flex-shrink-0 w-3 h-3 rounded-full bg-[#8FBC8F] mt-1'></div>
            <div className='flex flex-col'>
              <span className='text-sm font-medium text-gray-900'>First Time Users</span>
              <span className='text-sm text-gray-600'>
                {performanceData.first_time_restaurants.percentage}% • {performanceData.first_time_restaurants.count.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
